import { useEffect, useRef } from 'react'
import L from 'leaflet'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-cluster'
import type { Device } from '@/types/device.types'
import 'leaflet/dist/leaflet.css'
import './DeviceMap.css'

// Color-coded marker icons (copied from ColdFusion implementation)
const iconMap = {
  green: L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  }),
  yellow: L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-yellow.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  }),
  orange: L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  }),
  red: L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  }),
  grey: L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-grey.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  })
}

interface DeviceWithLocation extends Device {
  lat: number
  long: number
}

interface DeviceMapProps {
  devices: Device[]
}

// Helper function to get liquid level percentage from measurement
const getLiquidLevelPercentage = (device: Device): number => {
  if (!device.lastMeasurement || typeof device.lastMeasurement !== 'object') {
    return -1
  }

  const measurement = device.lastMeasurement as any

  // For watersense devices, use the liquid_level_percentage calculated by the API
  if (device.deviceType === 'watersense') {
    if (measurement.liquid_level_percentage !== undefined && measurement.liquid_level_percentage !== null) {
      return measurement.liquid_level_percentage
    }
  }

  return -1
}

// Helper function to determine marker color based on liquid level
const getMarkerColor = (liquidLevelPercentage: number): keyof typeof iconMap => {
  if (liquidLevelPercentage < 0) return 'grey'
  if (liquidLevelPercentage >= 75) return 'green'
  if (liquidLevelPercentage >= 50) return 'yellow'
  if (liquidLevelPercentage >= 25) return 'orange'
  return 'red'
}

// Helper function to format time ago
const formatTimeAgo = (dateString: string | undefined): string => {
  if (!dateString) return 'Never'

  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
  return 'Just now'
}

// Component to fit bounds when devices change
const FitBounds = ({ devices }: { devices: DeviceWithLocation[] }) => {
  const map = useMap()

  useEffect(() => {
    if (devices.length === 0) return

    const bounds = L.latLngBounds(devices.map(d => [d.lat, d.long]))
    const extendedBounds = bounds.pad(0.05)
    map.fitBounds(extendedBounds, {
      padding: [10, 10],
      maxZoom: 19
    })
  }, [devices, map])

  return null
}

export const DeviceMap = ({ devices }: DeviceMapProps) => {
  // Filter devices with valid lat/long
  const devicesWithLocation: DeviceWithLocation[] = devices.filter(
    (d): d is DeviceWithLocation =>
      d.lat !== undefined &&
      d.lat !== null &&
      d.long !== undefined &&
      d.long !== null &&
      typeof d.lat === 'number' &&
      typeof d.long === 'number' &&
      !isNaN(d.lat) &&
      !isNaN(d.long) &&
      d.lat !== 0 &&
      d.long !== 0
  )

  if (devicesWithLocation.length === 0) {
    return (
      <div className="flex items-center justify-center h-[400px] bg-muted/20 rounded-lg">
        <p className="text-sm text-muted-foreground">No devices with GPS locations found</p>
      </div>
    )
  }

  const center: [number, number] = [
    devicesWithLocation[0].lat,
    devicesWithLocation[0].long
  ]

  return (
    <div className="w-full h-[400px] rounded-lg overflow-hidden border">
      <MapContainer
        center={center}
        zoom={15}
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
      >
        {/* Satellite layer (default) */}
        <TileLayer
          attribution='&copy; Esri'
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          maxZoom={19}
        />

        {/* Marker cluster group with custom cluster icons */}
        <MarkerClusterGroup
          chunkedLoading
          maxClusterRadius={20}
          iconCreateFunction={(cluster) => {
            const markers = cluster.getAllChildMarkers() as any[]

            // Priority: red > orange > yellow > green
            let hasRed = false
            let hasYellow = false

            markers.forEach((marker) => {
              const iconUrl = marker.options.icon?.options?.iconUrl || ''
              if (iconUrl.includes('red')) hasRed = true
              else if (iconUrl.includes('orange')) hasYellow = true
            })

            let clusterClass = 'marker-cluster-green'
            if (hasRed) clusterClass = 'marker-cluster-red'
            else if (hasYellow) clusterClass = 'marker-cluster-yellow'

            return L.divIcon({
              html: `<div><span>${cluster.getChildCount()}</span></div>`,
              className: `marker-cluster ${clusterClass}`,
              iconSize: L.point(40, 40)
            })
          }}
        >
          {devicesWithLocation.map((device) => {
            const liquidLevelPercentage = getLiquidLevelPercentage(device)
            const markerColor = getMarkerColor(liquidLevelPercentage)
            const icon = iconMap[markerColor]

            return (
              <Marker
                key={device.id}
                position={[device.lat, device.long]}
                icon={icon}
              >
                <Popup>
                  <div className="p-2">
                    <strong className="text-base">{device.name}</strong>
                    <hr className="my-2" />
                    {liquidLevelPercentage >= 0 && (
                      <p className="text-sm">
                        Level: <strong>{Math.round(liquidLevelPercentage)}%</strong>
                      </p>
                    )}
                    <p className="text-sm">
                      Last Seen: {formatTimeAgo(device.lastSeen)}
                    </p>
                    <a
                      href={`/devices/${device.id}`}
                      className="text-sm text-blue-600 hover:underline mt-2 inline-block"
                    >
                      View Device
                    </a>
                  </div>
                </Popup>
              </Marker>
            )
          })}
        </MarkerClusterGroup>

        <FitBounds devices={devicesWithLocation} />
      </MapContainer>
    </div>
  )
}
